import React, { useEffect, useRef } from 'react';
import { SupplierProductBannerProps } from './types';
import { styles } from './SupplierProductBanner.css';
import { HitPriceTag } from '../ProductCompare/HitPriceTag/HitPriceTag';
import { formatDate } from '../ProductHtmlBanner/utils';
import { useNavigate } from '../PlatformFunctionality';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { OverlayBox } from './OverlayBox';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { NewAtJbBadge } from '../BannerBadge/NewAtJbBadge';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { useTrackingContext } from '../Tracking';
import { trackingDataToDataAttributes } from '../HeroBanner/utils';
import { ContentfulImage } from '../ContentfulImage/ContentfulImage';
import { AssetTransformer } from '../Contentful/transformers/AssetTransformer';
import { TermsAndConditionsLink } from '../TermsAndConditions/TermsAndConditionsLink';

const GAMES_DEPARTMENT_CODES = [90, 92];
const MOVIES_DEPARTMENT_CODES = [110];

export const SupplierProductBanner = ({
  contentProducts: products,
  desktopImage,
  mobileImage,
  color,
  shouldShow1ProductInBoxWithImage,
  shouldShowNewAtJbBadge,
  tagType,
  badgeStyle,
  badge,
  overallEndDate,
  trackingData,
  termsAndConditions,
}: SupplierProductBannerProps) => {
  const productOne = products[0];
  const isMobile = useBreakpoint('md', 'down');
  const isTablet = useBreakpoint('lg', 'down');
  const isDesktop = useBreakpoint('lg', 'up');
  const isSingleProduct = products.length === 1 && !isMobile;
  const isDoubleProduct = products.length === 2;
  const backgroundImage = isMobile ? mobileImage : desktopImage;
  const navigateTo = useNavigate();
  const isWhiteTheme = color === 'White';
  const isGameProduct = GAMES_DEPARTMENT_CODES.includes(productOne?.hit?.product?.departmentCode ?? 0);
  const isMovieProduct = MOVIES_DEPARTMENT_CODES.includes(productOne?.hit?.product?.departmentCode ?? 0);
  const isLongText = products.some(product => product.hit?.title && product.hit.title.length > 45);
  const subtitle = isGameProduct
    ? productOne?.hit?.facets['Label']
    : isMovieProduct
      ? productOne.hit?.product?.format
      : undefined;
  const cardVariant = isMobile ? 'mobile' : isTablet ? 'medium' : 'large';

  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useIsOnScreen(containerRef, { persistOnceSeen: true });
  const { tracker } = useTrackingContext();
  const haveTrackedImpression = useRef<boolean>(false);
  const singleProductHasDelivery = !!productOne?.hit?.deliveryTag;
  const isDesktopWithDelivery = isDesktop && products.some(product => !!product?.hit?.deliveryTag);
  const isLongTextSingleProduct =
    isDesktop && isSingleProduct && productOne?.hit?.title ? productOne.hit.title.length > 45 : false;

  const handleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    tracker.trackCustomEvent({ event: 'internal promotion click', gaipElement: containerRef.current });
    if (!isDoubleProduct || isMobile) {
      navigateTo(`/products/${productOne?.hit?.handle}`);
    }
  };

  useEffect(() => {
    if (isOnScreen && !haveTrackedImpression.current) {
      haveTrackedImpression.current = true;
      tracker.trackCustomEvent({ event: 'internal promotion impression', gaipElement: containerRef.current });
    }
  }, [isOnScreen, tracker]);

  return (
    <div className={styles.outerContainer}>
      <ContentfulImage
        {...AssetTransformer.toContentfulImageProps(backgroundImage)}
        className={styles.backgroundImage}
        loading="eager"
        fetchPriority="high"
        sizes="(max-width: 1124px) 100vw, 2324px"
        isProgressive={false}
        alt={productOne.hit?.title}
      />
      <div
        className={styles.container({ isSingleProduct, cardVariant })}
        ref={containerRef}
        onClick={handleClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e as any);
          }
        }}
        role="button"
        tabIndex={0}
        {...trackingDataToDataAttributes(
          trackingData
            ? { ...trackingData, category: trackingData.category || tracker.category, url: productOne.hit?.handle }
            : undefined
        )}
      >
        {shouldShowNewAtJbBadge && isDoubleProduct && !isMobile ? (
          <div className={styles.newAtJbBadgeContainer()}>
            <NewAtJbBadge className={styles.newAtJbBadge} />
          </div>
        ) : null}
        <div className={styles.cardContainer}>
          {shouldShow1ProductInBoxWithImage && productOne && !isMobile ? (
            <OverlayBox
              product={productOne}
              cardVariant={cardVariant}
              color={color}
              tagType={tagType}
              badgeStyle={badgeStyle}
              badge={badge}
              subtitle={subtitle}
            />
          ) : (
            <div className={styles.columnEndDateContainer}>
              {shouldShowNewAtJbBadge && (isMobile || ((isDesktop || isTablet) && isSingleProduct)) ? (
                <div className={styles.newAtJbBadgeContainer({ cardVariant })}>
                  <NewAtJbBadge className={styles.newAtJbBadge} />
                </div>
              ) : null}
              <div className={styles.columnContainer}>
                {(isMobile ? [productOne] : products).map(product => {
                  const isGameProduct = GAMES_DEPARTMENT_CODES.includes(product?.hit?.product?.departmentCode ?? 0);
                  const isMovieProduct = MOVIES_DEPARTMENT_CODES.includes(product?.hit?.product?.departmentCode ?? 0);
                  const subtitle = isGameProduct
                    ? productOne?.hit?.facets['Label']
                    : isMovieProduct
                      ? productOne.hit?.product?.format
                      : undefined;
                  const isPreOrder = product?.hit?.availability?.canPreOrder;
                  const hasStrikethroughOrRHD = !!(
                    product?.hit?.pricing.displayWasPrice || product?.hit?.priceBannerTag?.label
                  );

                  const onTitleClick = () => {
                    navigateTo(`/products/${product?.hit?.handle}`);
                  };
                  return (
                    <>
                      <div
                        className={styles.rightSide({
                          cardVariant,
                          isSingleProduct,
                          hasStrikethroughOrRHD,
                        })}
                        key={product.variantId}
                      >
                        <div>
                          <div
                            className={styles.title({
                              isWhiteTheme,
                              isLongTextSingleProduct,
                              isLongText,
                              hasStrikethroughOrRHD,
                            })}
                            onClick={onTitleClick}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                onTitleClick();
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {product?.hit?.title}
                          </div>
                          {subtitle ? (
                            <div className={styles.subtitle({ isWhiteTheme, isMobile })}>{subtitle}</div>
                          ) : null}
                        </div>
                        <div className={styles.priceEndDateButtonContainer({ isDesktopWithDelivery })}>
                          {product.hit ? (
                            <div
                              className={styles.priceTagContainer({
                                isMobile,
                                isTallerPriceBox: hasStrikethroughOrRHD && !isMobile,
                              })}
                            >
                              <HitPriceTag
                                hit={product.hit}
                                variant={isMobile ? 'extraSmall' : 'small'}
                                showDelivery
                                showEA={product.enableEAPriceBox}
                              />
                            </div>
                          ) : null}
                          <div className={styles.endDateButtonContainer({ cardVariant })}>
                            {!isDoubleProduct || isMobile ? (
                              <div className={styles.endDateText({ isWhiteTheme, singleProductHasDelivery })}>
                                {product.endDate ? (
                                  <>
                                    <b>Ends: </b>
                                    <p className={styles.endDateTextWrap({ isWhiteTheme })}>
                                      {formatDate(product.endDate)}
                                    </p>
                                  </>
                                ) : null}
                              </div>
                            ) : null}
                            <div className={styles.buttonContainer}>
                              <Button
                                onClick={() => {
                                  navigateTo(`/products/${product?.hit?.handle}`);
                                }}
                                type={color === 'Black' ? 'primary' : 'secondary'}
                                label={isPreOrder ? 'Pre-order' : 'Shop now'}
                                fullWidth
                                size={isMobile ? 'sm' : undefined}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
              {overallEndDate && isDoubleProduct && !isMobile ? (
                <div className={styles.endDateContainer}>
                  <CalloutFont>Ends {formatDate(overallEndDate, true)}</CalloutFont>
                </div>
              ) : null}
            </div>
          )}
        </div>
        {termsAndConditions ? (
          <div className={styles.termsLinkContainer({ isWhite: termsAndConditions?.linkColor === 'white' })}>
            <TermsAndConditionsLink termsAndConditions={termsAndConditions} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
